import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import client from "@/lib/api";
import { BRAND } from "@/config";
import Seo from "@/components/Seo";
import Ticker from "@/components/Ticker";
import { RevealLines, FadeUp } from "@/components/Reveal";
import TrustBadges from "@/components/TrustBadges";
import DropCard from "@/components/DropCard";
import Newsletter from "@/components/Newsletter";

const HERO = "https://images.unsplash.com/photo-1638376776402-9a4b75fe21bb?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

const chapters = [
  {
    n: "01",
    title: "UNCOMPROMISING",
    body: "YAZA is a DarkGrowers brand committed to uncompromising quality and customer service. Consistently rated 10 out of 10, we take pride in delivering premium flower that sets the industry standard.",
  },
  {
    n: "02",
    title: "RARE BY DESIGN",
    body: "We craft clean, exotic product that is rare to find — distinct from what 95% of the market offers. Every phenotype is hunted, every cure is dialed.",
  },
  {
    n: "03",
    title: "TIER 1 SERVICE",
    body: "When service and genetics are Tier 1, that's YAZA. Real people, real answers, real product — routed straight to you.",
  },
];

export default function Home() {
  const [drops, setDrops] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  useEffect(() => {
    client.get("/drops").then(({ data }) => setDrops(data.slice(0, 3))).catch(() => {});
  }, []);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Brand",
    name: BRAND.name,
    alternateName: BRAND.short,
    slogan: BRAND.tagline,
    parentOrganization: BRAND.parent,
    description: BRAND.metaDescription,
  };

  return (
    <>
      <Seo
        title="YAZA EXOTICS — Premium Exotic Cannabis Genetics"
        description={BRAND.metaDescription}
        image={HERO}
        jsonLd={jsonLd}
      />

      {/* HERO */}
      <section ref={heroRef} className="evidence-frame relative h-[92vh] min-h-[600px] w-full overflow-hidden" data-testid="hero">
        <motion.div style={{ y, scale }} className="absolute inset-0">
          <img src={HERO} alt="YAZA exotics" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black" />
        </motion.div>

        <div className="relative z-10 mx-auto flex h-full max-w-[1600px] flex-col justify-end px-5 pb-16 md:px-10 md:pb-24">
          <h1 className="font-display text-[16vw] leading-[0.82] tracking-tight md:text-[11vw]">
            <RevealLines lines={["PREMIUM"]} delay={0.15} />
            <RevealLines lines={["EXOTICS"]} delay={0.3} stroke />
          </h1>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            className="mt-8 flex flex-col gap-6 md:flex-row md:items-center md:justify-between"
          >
            <p className="max-w-md text-sm text-yaza-dim md:text-base">{BRAND.metaDescription}</p>
            <Link
              to="/drops"
              data-testid="hero-cta"
              className="group inline-flex w-fit items-center gap-3 rounded-none bg-yaza-accent px-8 py-4 text-sm font-bold uppercase tracking-[0.2em] text-black transition-colors hover:bg-yaza-accentHover"
            >
              Premium Exotics
              <ArrowRight className="transition-transform group-hover:translate-x-1" size={18} />
            </Link>
          </motion.div>
        </div>
      </section>

      <Ticker text="YAZA" speed={70} />

      {/* BREEDING THE FUTURE — manifesto chapters */}
      <section className="mx-auto max-w-[1600px] px-5 py-24 md:px-10 md:py-40" data-testid="manifesto">
        <FadeUp>
          <p className="mb-4 text-xs font-semibold uppercase tracking-[0.3em] text-yaza-accent">The Manifesto</p>
          <h2 className="font-display text-6xl leading-[0.9] md:text-8xl">
            BREEDING<br />THE <span className="text-stroke-accent">FUTURE</span>
          </h2>
        </FadeUp>

        <div className="mt-20 flex flex-col gap-16">
          {chapters.map((c, i) => (
            <FadeUp key={c.n} delay={i * 0.05}>
              <div className="grid gap-6 border-t border-yaza-border pt-8 md:grid-cols-12" style={{ marginLeft: `${i * 4}%` }}>
                <div className="md:col-span-3">
                  <span className="font-display text-6xl text-yaza-accent md:text-7xl">{c.n}</span>
                  <h3 className="mt-2 font-display text-2xl tracking-wide">{c.title}</h3>
                </div>
                <p className="text-lg leading-relaxed text-yaza-dim md:col-span-7 md:col-start-5">{c.body}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* CURRENT DROP PREVIEW */}
      <section className="border-t border-yaza-border bg-yaza-surface px-5 py-24 md:px-10 md:py-32" data-testid="current-drops">
        <div className="mx-auto max-w-[1600px]">
          <div className="flex items-end justify-between">
            <FadeUp>
              <h2 className="font-display text-5xl leading-none md:text-7xl">CURRENT<br />DROP</h2>
            </FadeUp>
            <Link to="/drops" data-testid="see-all-drops" className="group inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-yaza-accent">
              See all drops <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {drops.map((d, i) => (
              <DropCard key={d.id} drop={d} index={i} />
            ))}
          </div>
        </div>
      </section>

      <Ticker text="YAZA" speed={70} direction="right" accent />

      <TrustBadges />

      <div className="hazard-stripes h-2" />

      {/* CLOSING COPY */}
      <section className="mx-auto max-w-[1100px] px-5 py-28 text-center md:px-10 md:py-40" data-testid="closing-copy">
        <FadeUp>
          <p className="font-display text-4xl leading-tight md:text-6xl">
            STEP INTO THE FUTURE WITH OUR EXOTIC BOUTIQUE FLOWER — A FUSION OF TRADITIONAL GENETICS AND MODERN CANDY.
          </p>
        </FadeUp>
        <FadeUp delay={0.1}>
          <p className="mx-auto mt-10 max-w-2xl text-lg text-yaza-dim">
            We grow together, curating craft cannabis from YAZA's vision. Tired of settling for the ordinary?
            Join us in being a part of something clean, safe and different.
          </p>
        </FadeUp>
      </section>

      {/* NEWSLETTER */}
      <section className="border-t-8 border-yaza-accent bg-yaza-surface px-5 py-24 md:px-10 md:py-32" data-testid="newsletter-section">
        <div className="mx-auto max-w-[1600px]">
          <Newsletter
            source="home"
            heading="JOIN OUR YAZA LIST"
            subtext="Subscribe to get special offers, free giveaways, and once-in-a-lifetime deals."
          />
        </div>
      </section>
    </>
  );
}
